/**
 * Snapshot de permisos a nivel de módulo.
 *
 * WHY no es un contexto de React: `<Can>` se usa en 37 componentes, incluido el
 * menú lateral. Suscribirlo a un contexto lo hace re-renderizar con cada cambio
 * de sesión y arrastra a sus padres, lo que puede reiniciar el estado local de
 * componentes con estado (los acordeones del menú, por ejemplo).
 *
 * Un objeto plano mantiene `<Can>` como función pura: lee el valor vigente sin
 * suscribirse a nada. El coste es que un cambio de permisos no re-renderiza
 * solo; se asume a propósito, porque los permisos cambian en dos momentos
 * concretos (login y edición de un rol) y ambos ya provocan navegación o recarga.
 */
const state = {
  permissions: new Set(),
  scopes: [],
  /** ¿La empresa trabaja con subagrupaciones? (doc 0380 D7 · paso 2.A) */
  teamsEnabled: false,
  /**
   * Departamentos que el usuario alcanza POR SU EQUIPO.
   *
   * WHY se guarda: el backend ya lo enviaba en `/me/permissions` y aquí se tiraba.
   * Sin él, cualquier pantalla que quiera saber «¿este ticket es de los míos?»
   * acaba mirando `user.queues` —la ficha— y se pierde todo lo que la persona
   * atiende por pertenecer a un equipo. Fue exactamente el fallo del contador de
   * notificaciones: el backend devolvía los tickets y el navegador los descartaba.
   */
  teamQueueIds: [],
  loaded: false
};

export const setAccessSnapshot = ({
  permissions = [],
  scopes = [],
  teamsEnabled = false,
  teamQueueIds = [],
  loaded = false
}) => {
  state.permissions = new Set(permissions);
  state.scopes = scopes;
  state.teamsEnabled = teamsEnabled;
  state.teamQueueIds = teamQueueIds;
  state.loaded = loaded;
};

export const clearAccessSnapshot = () => {
  state.permissions = new Set();
  state.scopes = [];
  state.teamsEnabled = false;
  state.teamQueueIds = [];
  state.loaded = false;
};

/**
 * Carga los permisos del backend y los publica en el snapshot.
 *
 * Se llama al establecerse la sesión. Falla en silencio a propósito: si el
 * endpoint no responde, `<Can>` recurre a `rules.js` y el panel se comporta
 * exactamente como antes. **La carga de permisos nunca puede impedir entrar.**
 */
export const loadAccessSnapshot = async api => {
  try {
    const { data } = await api.get("/me/permissions");
    setAccessSnapshot({
      permissions: data?.permissions ?? [],
      scopes: data?.scopes ?? [],
      teamsEnabled: Boolean(data?.teamsEnabled),
      teamQueueIds: (data?.scope?.queueIds ?? []).map(Number),
      loaded: true
    });
  } catch (err) {
    clearAccessSnapshot();
  }
};

/**
 * Aviso de que el snapshot cambió sin recargar la página.
 *
 * WHY existe: el modo equipos se enciende desde Ajustes y el menú lateral tiene
 * que reaccionar en el momento. El snapshot no es un contexto de React a
 * propósito (ver cabecera), así que nadie se entera solo — este evento es el
 * puente, y lo escucha quien necesite repintarse.
 */
export const ACCESS_SNAPSHOT_EVENT = "watoolx:access-snapshot";

export const refreshAccessSnapshot = async api => {
  await loadAccessSnapshot(api);
  window.dispatchEvent(new CustomEvent(ACCESS_SNAPSHOT_EVENT));
};

export const getAccessSnapshot = () => ({
  loaded: state.loaded,
  scopes: state.scopes,
  teamsEnabled: state.teamsEnabled,
  teamQueueIds: state.teamQueueIds,
  isPlatformAdmin: state.scopes.includes("plataforma"),
  can: permission => {
    if (state.scopes.includes("plataforma")) return true;
    return state.permissions.has(permission);
  }
});
